import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  Box,
  Grid,
  CircularProgress,
  makeStyles,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Save as SaveIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Message as WhatsAppIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from "@material-ui/icons";
import useVPSNotifications from "../hooks/useVPSNotifications";
import useSMTPConfig from "../hooks/useSMTPConfig";
import useEmailTemplates from "../hooks/useEmailTemplates";
import { WhatsAppsContext } from "../../../context/WhatsApp/WhatsAppsContext";
import { i18n } from "../../../translate/i18n";
import { useStyles } from "./NotificationAlertsConfigStyles";
import useNotificationAlerts from "./useNotificationAlerts";
import {
  VPS_ALERTS_CHANNEL_ANCHOR_ID,
  buildChannelValidationMessages,
} from "./notificationAlerts.helpers";


const NotificationAlertsConfig = () => {
  const classes = useStyles();
  const alertsApi = useNotificationAlerts(classes);
  // El hook hace early-return de JSX (spinner de carga / card de error de carga):
  // en esos casos NO devuelve el objeto de estado, sino un elemento React que hay
  // que renderizar tal cual. Sin esta guardia, la desestructuración deja formData
  // undefined → crash "reading 'enabled'". Mismo patrón que useMessageInput.
  if (React.isValidElement(alertsApi)) return alertsApi;
  const {
    activeSmtpConfigs,
    config,
    connectedWhatsApps,
    dateLocale,
    emailTemplates,
    fetchConfig,
    formData,
    handleChange,
    handleNotificationTypeChange,
    handleSave,
    handleSendTestNotification,
    infoModalContent,
    infoModalOpen,
    loadError,
    loading,
    saveConfig,
    saving,
    scrollToNotificationChannelSection,
    sendTestNotification,
    sendingTest,
    setFormData,
    setInfoModalContent,
    setInfoModalOpen,
    setTestModalOpen,
    setTestResult,
    showInfoModal,
    showValidationIssuesModal,
    smtpConfigs,
    testModalOpen,
    testResult,
    toBoolean,
    validateEmail,
    validateWhatsAppNumber,
    whatsApps,
  } = alertsApi;

  return (
    <div className={classes.container}>
      {/* Sección 1: Habilitación General */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("settings.vpsAlerts.generalSection")}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={handleChange('enabled')}
                color="primary"
              />
            }
            label={i18n.t("settings.vpsAlerts.toggleLabel")}
          />

          {/* Info Box */}
          <Box className={classes.infoBox} mt={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <InfoIcon style={{ marginRight: 8, color: '#2196F3' }} />
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {i18n.t("settings.vpsAlerts.infoWhyTitle")}
              </Typography>
            </Box>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {i18n.t("settings.vpsAlerts.infoIntro")}
            </Typography>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>{i18n.t("settings.vpsAlerts.infoLi1")}</li>
              <li>{i18n.t("settings.vpsAlerts.infoLi2")}</li>
              <li>{i18n.t("settings.vpsAlerts.infoLi3")}</li>
              <li>{i18n.t("settings.vpsAlerts.infoLi4")}</li>
            </ul>
            <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic' }}>
              {i18n.t("settings.vpsAlerts.infoClosing")}
            </Typography>
          </Box>

          {/* Warning Box */}
          {formData.enabled && (
            <Box className={classes.warningBox} mt={2}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                <span role="img" aria-label="warning">⚠️</span> {i18n.t("settings.vpsAlerts.warningEnabled")}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Sección 2: Configuración de Notificaciones (solo si enabled) */}
      {formData.enabled && (
        <Card id={VPS_ALERTS_CHANNEL_ANCHOR_ID} className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("settings.vpsAlerts.notificationsSection")}
            </Typography>

            <FormControl component="fieldset" className={classes.formField}>
              <RadioGroup
                row
                value={formData.notificationType}
                onChange={handleNotificationTypeChange}
              >
                <FormControlLabel value="email" control={<Radio />} label={i18n.t("settings.vpsAlerts.channelEmail")} />
                <FormControlLabel value="whatsapp" control={<Radio />} label={i18n.t("settings.vpsAlerts.channelWhatsapp")} />
                <FormControlLabel value="both" control={<Radio />} label={i18n.t("settings.vpsAlerts.channelBoth")} />
                <FormControlLabel value="none" control={<Radio />} label={i18n.t("settings.vpsAlerts.channelNone")} />
              </RadioGroup>
            </FormControl>

            <Divider style={{ margin: '16px 0' }} />

            {/* Formulario Email */}
            {(formData.notificationType === 'email' || formData.notificationType === 'both') && (
              <Box mb={3}>
                <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
                  <EmailIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {i18n.t("settings.vpsAlerts.emailSection")}
                </Typography>

                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>{i18n.t("settings.vpsAlerts.smtpRequiredLabel")}</InputLabel>
                  <Select
                    value={formData.smtpConfigId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, smtpConfigId: e.target.value || null }))}
                    label={i18n.t("settings.vpsAlerts.smtpRequiredLabel")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("settings.vpsAlerts.selectSmtp")}</em>
                    </MenuItem>
                    {activeSmtpConfigs.map((smtp) => (
                      <MenuItem key={smtp.id} value={smtp.id}>
                        {smtp.name}{smtp.isDefault ? ` (${i18n.t("settings.smtpManager.defaultChip")})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>{i18n.t("settings.vpsAlerts.emailTemplateOptional")}</InputLabel>
                  <Select
                    value={formData.emailTemplateId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, emailTemplateId: e.target.value || null }))}
                    label={i18n.t("settings.vpsAlerts.emailTemplateOptional")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("settings.vpsAlerts.useDefaultTemplates")}</em>
                    </MenuItem>
                    {emailTemplates
                      .filter(template => 
                        template.purpose && 
                        (template.purpose === 'vps_alert' || template.purpose.startsWith('vps_alert'))
                      )
                      .map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name} - {template.purpose}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label={i18n.t("settings.vpsAlerts.adminEmailLabel")}
                  value={formData.adminEmail}
                  onChange={handleChange('adminEmail')}
                  className={classes.formField}
                  error={!!(formData.adminEmail && !validateEmail(formData.adminEmail))}
                  helperText={
                    formData.adminEmail && !validateEmail(formData.adminEmail)
                      ? i18n.t("settings.vpsAlerts.invalidEmail")
                      : i18n.t("settings.vpsAlerts.adminEmailHint")
                  }
                />
              </Box>
            )}

            {/* Formulario WhatsApp */}
            {(formData.notificationType === 'whatsapp' || formData.notificationType === 'both') && (
              <Box>
                <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
                  <WhatsAppIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {i18n.t("settings.vpsAlerts.whatsappSection")}
                </Typography>

                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>{i18n.t("settings.vpsAlerts.whatsappConnectionLabel")}</InputLabel>
                  <Select
                    value={formData.whatsappId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, whatsappId: e.target.value || null }))}
                    label={i18n.t("settings.vpsAlerts.whatsappConnectionLabel")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("settings.vpsAlerts.selectWhatsapp")}</em>
                    </MenuItem>
                    {connectedWhatsApps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name} ({whatsapp.number})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label={i18n.t("settings.vpsAlerts.whatsappTemplateLabel")}
                  value={formData.whatsappMessageTemplate}
                  onChange={handleChange('whatsappMessageTemplate')}
                  className={classes.formField}
                  helperText={i18n.t("settings.vpsAlerts.whatsappTemplateVars")}
                />

                <TextField
                  fullWidth
                  label={i18n.t("settings.vpsAlerts.adminWhatsappLabel")}
                  value={formData.adminWhatsapp}
                  onChange={handleChange('adminWhatsapp')}
                  className={classes.formField}
                  placeholder="+1234567890"
                  error={!!(formData.adminWhatsapp && !validateWhatsAppNumber(formData.adminWhatsapp))}
                  helperText={
                    formData.adminWhatsapp && !validateWhatsAppNumber(formData.adminWhatsapp)
                      ? i18n.t("settings.vpsAlerts.invalidWhatsapp")
                      : i18n.t("settings.vpsAlerts.adminWhatsappHint")
                  }
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sección 3: Umbrales de Alertas (solo si enabled) */}
      {formData.enabled && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("settings.vpsAlerts.thresholdsSection")}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.diskPct")}
                  value={formData.diskAlertThreshold}
                  onChange={handleChange('diskAlertThreshold')}
                  inputProps={{ min: 0, max: 100 }}
                  helperText={i18n.t("settings.vpsAlerts.diskHelp")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.binaryGb")}
                  value={formData.binaryLogAlertThreshold}
                  onChange={handleChange('binaryLogAlertThreshold')}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={i18n.t("settings.vpsAlerts.binaryHelp")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.cpuPct")}
                  value={formData.cpuAlertThreshold}
                  onChange={handleChange('cpuAlertThreshold')}
                  inputProps={{ min: 0, max: 100 }}
                  helperText={i18n.t("settings.vpsAlerts.cpuHelp")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.memPct")}
                  value={formData.memoryAlertThreshold}
                  onChange={handleChange('memoryAlertThreshold')}
                  inputProps={{ min: 0, max: 100 }}
                  helperText={i18n.t("settings.vpsAlerts.memHelp")}
                />
              </Grid>
            </Grid>

            <Box mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableDiskAlerts}
                    onChange={handleChange('enableDiskAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableDisk")}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableBinaryLogAlerts}
                    onChange={handleChange('enableBinaryLogAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableBinary")}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableCpuAlerts}
                    onChange={handleChange('enableCpuAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableCpu")}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableMemoryAlerts}
                    onChange={handleChange('enableMemoryAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableMem")}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableCriticalAlerts}
                    onChange={handleChange('enableCriticalAlerts')}
                    color="primary"
                  />
                }
                label={i18n.t("settings.vpsAlerts.enableCritical")}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Sección 4: Frecuencia de Notificaciones y Limpieza Automática (solo si enabled) */}
      {formData.enabled && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("settings.vpsAlerts.frequencySection")}
            </Typography>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={i18n.t("settings.vpsAlerts.alertFreqLabel")}
                  value={formData.alertFrequency}
                  onChange={handleChange('alertFrequency')}
                  inputProps={{ min: 1, max: 48 }}
                  helperText={i18n.t("settings.vpsAlerts.alertFreqHelp")}
                />
              </Grid>
            </Grid>

            <Divider style={{ margin: '16px 0' }} />

            <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
              {i18n.t("settings.vpsAlerts.cleanupSubtitle")}
            </Typography>

            <Box className={classes.infoBox} mb={2}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                {i18n.t("settings.vpsAlerts.cleanupInfoTitle")}
              </Typography>
              <Typography variant="body2">
                {i18n.t("settings.vpsAlerts.cleanupInfoIntro")}
              </Typography>
              <ul style={{ marginTop: 8, paddingLeft: 20, marginBottom: 0 }}>
                <li>{i18n.t("settings.vpsAlerts.cleanupInfoLi1")}</li>
                <li>{i18n.t("settings.vpsAlerts.cleanupInfoLi2")}</li>
              </ul>
              <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic', color: '#c62828' }}>
                {i18n.t("settings.vpsAlerts.cleanupRisk")}
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enableAutoCleanup}
                  onChange={handleChange('enableAutoCleanup')}
                  color="primary"
                />
              }
              label={i18n.t("settings.vpsAlerts.enableAutoCleanup")}
            />

            {formData.enableAutoCleanup && (
              <Grid container spacing={2} style={{ marginTop: 8 }}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                    {i18n.t("settings.vpsAlerts.cleanupCooldownNote", { hours: formData.alertFrequency })}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enableDiskAutoCleanup}
                        onChange={handleChange('enableDiskAutoCleanup')}
                        color="primary"
                      />
                    }
                    label={i18n.t("settings.vpsAlerts.enableDiskAutoCleanup")}
                  />
                  <Typography variant="caption" color="textSecondary" display="block">
                    {i18n.t("settings.vpsAlerts.diskCleanupHelp")}
                  </Typography>
                </Grid>
                {formData.enableDiskAutoCleanup && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={i18n.t("settings.vpsAlerts.cleanupDiskThreshold")}
                    value={formData.autoCleanupDiskThreshold}
                    onChange={handleChange('autoCleanupDiskThreshold')}
                    inputProps={{ min: 0, max: 100 }}
                    helperText={i18n.t("settings.vpsAlerts.cleanupDiskHelp")}
                  />
                </Grid>
                )}
                {config && config.lastBinaryLogAutoCleanupAt && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("settings.vpsAlerts.lastCleanup", {
                        date: new Date(config.lastBinaryLogAutoCleanupAt).toLocaleString(dateLocale, {
                          dateStyle: 'short',
                          timeStyle: 'short'
                        })
                      })}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Botones de Acción - Solo visibles cuando el sistema está habilitado */}
            <Box className={classes.actions} style={{ marginTop: 24 }}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={() => handleSave()}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {saving ? i18n.t("settings.vpsAlerts.saving") : i18n.t("settings.vpsAlerts.saveConfig")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleSendTestNotification}
                disabled={sendingTest || (!formData.smtpConfigId && !formData.whatsappId)}
                startIcon={sendingTest ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {sendingTest ? i18n.t("settings.vpsAlerts.sending") : i18n.t("settings.vpsAlerts.sendTest")}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Modal de Resultado de Prueba de Notificación */}
      <Dialog
        open={testModalOpen}
        onClose={() => {}} // No permitir cerrar con click fuera o ESC durante la prueba
        className={classes.testModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {!testResult ? (
              <>
                <CircularProgress size={24} style={{ marginRight: 12 }} />
                <span>{i18n.t("settings.vpsAlerts.testModal.sending")}</span>
              </>
            ) : (
              <>
                {testResult.success ? (
                  <CheckCircleIcon style={{ color: '#4caf50', marginRight: 12 }} />
                ) : (
                  <CancelIcon style={{ color: '#f44336', marginRight: 12 }} />
                )}
                <span>
                  {testResult.success ? i18n.t("settings.vpsAlerts.testModal.success") : i18n.t("settings.vpsAlerts.testModal.error")}
                </span>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {testResult ? (
            <Box>
              <Alert
                severity={testResult.success ? "success" : "error"}
                className={classes.testResult}
                icon={testResult.success ? <CheckCircleIcon /> : <CancelIcon />}
              >
                <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                  {testResult.message}
                </Typography>
              </Alert>
              {testResult.success && (
                <Box mt={2} p={2} style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 8 }}>
                  <Typography variant="body2" style={{ color: '#4caf50' }}>
                    <strong><span role="img" aria-label="lightbulb">💡</span> {i18n.t("settings.vpsAlerts.testModal.tipPrefix")}</strong>{' '}
                    {formData.notificationType === 'both'
                      ? i18n.t("settings.vpsAlerts.testModal.tipBoth")
                      : formData.notificationType === 'whatsapp'
                      ? i18n.t("settings.vpsAlerts.testModal.tipWhatsapp")
                      : i18n.t("settings.vpsAlerts.testModal.tipEmail")}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" py={3}>
              <CircularProgress size={48} />
              <Typography variant="body1" style={{ marginTop: 16 }}>
                {i18n.t("settings.vpsAlerts.testModal.pleaseWait")}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setTestModalOpen(false)} 
            color="primary"
            variant="contained"
            disabled={!testResult} // Solo permitir cerrar cuando haya resultado
          >
            {testResult ? i18n.t("settings.vpsAlerts.testModal.close") : i18n.t("settings.vpsAlerts.testModal.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Informativo General */}
      <Dialog
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        className={classes.testModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {infoModalContent.type === 'success' && <CheckCircleIcon style={{ color: '#4caf50', marginRight: 12 }} />}
            {infoModalContent.type === 'error' && <CancelIcon style={{ color: '#f44336', marginRight: 12 }} />}
            {infoModalContent.type === 'warning' && <InfoIcon style={{ color: '#ff9800', marginRight: 12 }} />}
            {infoModalContent.type === 'info' && <InfoIcon style={{ color: '#2196f3', marginRight: 12 }} />}
            <span>{infoModalContent.title}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
            {infoModalContent.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setInfoModalOpen(false)} 
            color="primary"
            variant="contained"
          >
            {i18n.t("settings.vpsAlerts.accept")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NotificationAlertsConfig;
